import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import {
  NavigationEnd,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { ToastService } from './Shared/services/toast/toast.service';
import { GeneralService } from './_utlis/general/general.service';
import { StorageService } from './Core/_services/storage/storage.service';
import { catchError, forkJoin, Observable, of, Subscription } from 'rxjs';
import { LanguageService } from './Shared/services/language/language.service';
import { PreviousRouteService } from 'src/app/Shared/services/previous-route/previous-route.service';
import { PusherService } from './Shared/services/pusher/pusher.service';
import { isPlatformBrowser } from '@angular/common';
import { Navigation } from './_utlis/navigateToLanding';
import { OfflineBannerComponent } from './Shared/components/offline-banner/offline-banner.component';
import { BuyerAuthService } from './app-modules/buyer/buyer-core/buyer-authentication/services/buyer-auth/buyer-auth.service';
import { NetworkStatusService } from './Shared/_services/network-status/network-status.service';
import { isValidLocale, LocaleCode } from './Core/locale/locale.constants';

@Component({
    selector: 'app-root',
        standalone: true,
templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet, OfflineBannerComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'auction';
  isLoading!: boolean;
  isContentLoading!: boolean;
  metaDataSub!: Subscription;

  // Store subscriptions for cleanup
  private subscriptions: Subscription[] = [];

  availableLanguages: any;
  selectedLanguage: any;
  isBrowser!: boolean;

  constructor(
    private router: Router,
    private pusherService: PusherService,

    private generalService: GeneralService,
    private storegeService: StorageService,

    private toastService: ToastService,
    private translateService: LanguageService,
    private previousRouteService: PreviousRouteService,
    private networkStatusService: NetworkStatusService,
    @Inject(PLATFORM_ID) private platformId: any,
    private nav: Navigation,
    private auth: BuyerAuthService
  ) {

    this.isBrowser = isPlatformBrowser(this.platformId);

    // Store router subscriptions for cleanup
    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          if (this.isBrowser) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
          const url = event.urlAfterRedirects;
          // this.analyticsService.trackPageView(url);
        }
      })
    );

    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.toastService.toasts.forEach((toast) => {
            if (toast.title == 'Error') {
              this.toastService.remove(toast.toastId);
            }
          });
        }
      })
    );
    this.previousRouteService.setPreviousUrl(this.router.url);
  }
  redirectBackChecking() {
    if (this.isBrowser) {
      this.subscriptions.push(
        this.router.events.subscribe((event) => {
          if (event instanceof NavigationStart) {
            if (
              event.url.includes('/buyer/auth/login') &&
              this.auth.isLoggedInAndAuthorized()
            ) {
              // Prevent the login route from being loaded
              this.nav.NavigateToLanding();
            }
          }
        })
      );
    }
  }

  ngOnInit() {
    this.redirectBackChecking();
    if (this.isBrowser) {
      this.subscribeToPusherNetworkSignals();
      this.subscribeToSystemChannel();
    }
    this.initializeApp();
  }

  private subscribeToPusherNetworkSignals(): void {
    this.subscriptions.push(
      this.pusherService.connectionState$.subscribe((state: string) => {
        this.networkStatusService.reportPusherState(state);
      })
    );
  }

  initializeApp() {
    this.isLoading = true;
    this.ensureLanguageIsSet()
      .then(() => {
        this.getMissingData();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  ensureLanguageIsSet(): Promise<void> {
    return new Promise((resolve, reject) => {
      const localeFromUrl = this.getLocaleFromCurrentRoute();
      if (localeFromUrl) {
        if (this.translateService.availableLanguages.length > 0) {
          this.translateService.setLanguageFromLocaleCode(localeFromUrl);
          this.selectedLanguage = this.translateService.getSelectedLanguage();
          resolve();
          return;
        }

        this.translateService.getStaticContentLanguages().subscribe(
          (response) => {
            this.translateService.setAvailableLanguages(
              response.data.AvailableLanguages
            );
            this.translateService.setLanguageFromLocaleCode(localeFromUrl);
            this.selectedLanguage = this.translateService.getSelectedLanguage();
            resolve();
          },
          (error) => {
            this.toastService.showError('Failed to load default language');
            reject(error);
          }
        );
        return;
      }

      let lang: any = {};
      if (this.isBrowser) {
        lang = JSON.parse(
          localStorage.getItem(this.storegeService.getPrefixedKey('selectedLang')) ?? '{}'
        );
      }

      if (!lang || Object.keys(lang).length === 0) {
        this.translateService.getStaticContentLanguages().subscribe(
          (response) => {
            const defaultLanguage = response.data.DefaultLanguage;
            this.translateService.setAvailableLanguages(
              response.data.AvailableLanguages
            );
            this.translateService.setLanguage(defaultLanguage);
            this.translateService.setDirection(defaultLanguage);
            // Removed: setLatestUpdatedLanguages (part of backend translation system)
            if (this.isBrowser) {
              localStorage.setItem(
                this.storegeService.getPrefixedKey('selectedLang'),
                JSON.stringify(defaultLanguage)
              );
            }
            this.selectedLanguage = defaultLanguage;
            resolve();
          },
          (error) => {
            this.toastService.showError('Failed to load default language');
            reject(error);
          }
        );
      } else {
        this.translateService.setLanguage(lang);
        this.translateService.setDirection(lang);
        this.selectedLanguage = lang;
        resolve();
      }
    });
  }
  getMissingData() {
    this.isContentLoading = true;
    const reqArr = [];

    // Only fetch metadata if not available
    // Translations are now static and loaded via ngx-translate
    if (!this.storegeService.getMetaData().data_from_ip) {
      reqArr.push({
        name: 'getMetadata',
        observable: this.generalService.getMetadata().pipe(
          catchError((error) => {
            console.error('Error in getMetadata', error);
            return of(null);
          })
        ),
      });
    }

    if (reqArr.length) {
      this.handleRequests(reqArr);
    } else {
      this.isContentLoading = false;
    }
  }

  handleRequests(reqArr: { name: string; observable: Observable<any> }[]) {
    const observables = reqArr.map((req) => req.observable);
    this.isLoading = true;

    forkJoin(observables).subscribe({
      next: (responses) => {
        responses.forEach((response, index) => {
          switch (reqArr[index].name) {
            case 'getMetadata':
              // Handle metadata response
              if (response?.data) {
                this.storegeService.setMetaData({
                  data_from_ip: response.data,
                });
                // Popup check moved to complete() callback to ensure it runs
                // even when metadata already exists in localStorage
              }
              break;
            // Translation cases removed - now using static ngx-translate files
          }
        });
        this.isLoading = false; // Set loading state to false after successful requests
      },
      error: (error) => {
        this.toastService.showError(error?.error?.Error[0]);
        console.error('Error occurred:', error);
        this.isLoading = false; // Set loading state to false even if there's an error
      },
      complete: () => {
        this.isContentLoading = false; // Make sure content loading is false when done
      },
    });
  }
  getMetaData() {
    this.isLoading = true;
    this.generalService.getMetadata().subscribe(
      (res) => {
        this.isLoading = false;
      },
      (err) => {
        this.isLoading = false;

        this.toastService.showError(err.error.Error[0]);
      }
    );
  }

  /**
   * REMOVED: Backend translation fetching methods
   * Translations are now static and loaded via ngx-translate from JSON files
   *
   * Removed methods:
   * - getStaticContent()
   * - getStaticContentAndStaticContentPages()
   * - loadTranslations()
   */

  private getLocaleFromCurrentRoute(): LocaleCode | null {
    const pathOnly = this.isBrowser
      ? window.location.pathname
      : this.router.url.split(/[?#]/)[0] || '/';
    const firstSegment = pathOnly.split('/').filter(Boolean)[0]?.toLowerCase();
    return isValidLocale(firstSegment) ? firstSegment : null;
  }

  channel: any;
  subscribeToSystemChannel() {
    if (!this.channel) {
      this.channel = this.pusherService.pusher.subscribe('system');
    }

    // Bind clear-cache event
    this.channel.bind('clear-cache', (data: any) => {
      if (data) {
        this.reloadApp();
      }
    });

    // REMOVED: translation.updated event listener
    // Translations are now static and don't need real-time updates
  }

  unbindSystemChannelEvents() {
    if (this.channel) {
      this.channel.unbind('clear-cache');
      // translation.updated event removed
    }
  }

  reloadApp() {
    if (this.isBrowser) {
      window.location.reload();
    }
  }

  ngOnDestroy(): void {
    // Unsubscribe from all stored subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];

    this.previousRouteService.removePreviousFromLocalStorage();
    if (this.channel) {
      this.unbindSystemChannelEvents();
    }
  }
}
